import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@euroasiann/api-client';
import { TicketStatus, TicketEscalationLevel, TicketPriority } from '@euroasiann/shared';
import { MdSearch, MdFilterList, MdRefresh, MdVisibility, MdSupportAgent, MdConstruction } from 'react-icons/md';
import { cn } from '../../lib/utils';
import { ClearFiltersButton, TableBodySkeleton } from '@euroasiann/shared/ui';

// Premium Status Badge
const TicketStatusBadge = ({ status }: { status: string }) => {
    const statusConfig: any = {
        [TicketStatus.OPEN]: 'bg-blue-100 text-[#0077b3] dark:bg-blue-900/30 dark:text-blue-300 ring-1 ring-blue-200 dark:ring-blue-800',
        [TicketStatus.IN_PROGRESS]: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 ring-1 ring-yellow-200 dark:ring-yellow-800',
        [TicketStatus.RESOLVED]: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 ring-1 ring-emerald-200 dark:ring-emerald-800',
        [TicketStatus.CLOSED]: 'bg-[#0088cc]/10 text-[hsl(var(--foreground))] dark:bg-[#0088cc]/18/30 dark:text-[#0088cc]/45 ring-1 ring-[#0088cc]/20 dark:ring-gray-700',
        [TicketStatus.ESCALATED]: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 ring-1 ring-orange-200 dark:ring-orange-800',
    };

    const className = statusConfig[status] || statusConfig[TicketStatus.CLOSED];

    return (
        <span className={cn("px-3 py-1 rounded-full text-xs font-bold capitalize shadow-sm inline-flex items-center gap-1.5", className)}>
            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-75" />
            {status?.replace?.('_', ' ') || status || 'N/A'}
        </span>
    );
};

// Priority Badge (Subtle)
const PriorityBadge = ({ priority }: { priority: string }) => {
    const className = priority === TicketPriority.HIGH || priority === TicketPriority.CRITICAL
        ? 'text-red-500 bg-red-50 dark:bg-red-900/10 border-red-100 dark:border-red-900'
        : 'text-[hsl(var(--muted-foreground))] bg-[#0088cc]/5 dark:bg-[#0088cc]/25/10 border-[#0088cc]/20 dark:border-[#0088cc]/20';

    return (
        <span className={cn("px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border", className)}>
            {priority}
        </span>
    );
};

export const TechTicketDashboard = () => {
    const navigate = useNavigate();
    const [tickets, setTickets] = useState<any[]>([]);
    const [filteredTickets, setFilteredTickets] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState<string>('');

    useEffect(() => {
        fetchTickets();
    }, []);

    useEffect(() => {
        let result = tickets;

        if (searchQuery) {
            result = result.filter(t =>
                t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                t._id.includes(searchQuery.toLowerCase()) ||
                (t.organizationId?.name || '').toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        if (filterStatus) {
            result = result.filter(t => t.status === filterStatus);
        }

        setFilteredTickets(result);
    }, [tickets, searchQuery, filterStatus]);

    const fetchTickets = async () => {
        setIsLoading(true);
        try {
            const response = await apiClient.getSupportTickets('tech');
            const allTickets = response.tickets || [];
            // Filter strictly for Tech Team escalations
            const techTickets = allTickets.filter((t: any) => t.escalationLevel === TicketEscalationLevel.TECH_TEAM);
            setTickets(techTickets);
            setFilteredTickets(techTickets);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-300">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-1">
                        Tech Support Dashboard
                    </h1>
                    <p className="text-[hsl(var(--muted-foreground))] font-medium">
                        Manage and resolve escalated technical issues
                    </p>
                </div>

                <button
                    onClick={fetchTickets}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] transition-colors shadow-sm font-medium"
                >
                    <MdRefresh className={cn("w-4 h-4", isLoading && "animate-spin")} />
                    Refresh
                </button>
            </div>

            {/* Filter & Search Bar */}
            <div className="p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                    {/* Search */}
                    <div className="flex-1 relative">
                        <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[hsl(var(--muted-foreground))]" />
                        <input
                            type="text"
                            placeholder="Search tickets by ID, Subject, or Organization..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none text-sm"
                        />
                    </div>

                    {/* Status Filter */}
                    <div className="md:w-64 relative">
                        <MdFilterList className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                        <select
                            className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none text-sm appearance-none"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="">All Statuses</option>
                            {Object.values(TicketStatus).map(s => (
                                <option key={s} value={s}>{s?.replace?.('_', ' ') || s}</option>
                            ))}
                        </select>
                    </div>
                    <ClearFiltersButton
                        visible={searchQuery.trim() !== '' || filterStatus !== ''}
                        onClick={() => {
                            setSearchQuery('');
                            setFilterStatus('');
                        }}
                        variant="toolbar"
                    />
                </div>
            </div>

            {/* Tickets Table / List */}
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-[hsl(var(--muted))]/50 border-b border-[hsl(var(--border))]">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Ticket Details</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Organization</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Priority</th>
                                <th className="px-6 py-4 text-right text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[hsl(var(--border))]">
                            {isLoading ? (
                                <TableBodySkeleton rows={6} cols={5} />
                            ) : filteredTickets.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-20 text-center">
                                        <div className="flex flex-col items-center justify-center text-[hsl(var(--muted-foreground))]">
                                            <MdConstruction className="w-12 h-12 mb-3 opacity-20" />
                                            <p className="font-medium">No escalated tickets found</p>
                                            <p className="text-sm opacity-70">Tech queue is clear for now</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredTickets.map(ticket => (
                                    <tr key={ticket._id} className="group hover:bg-[hsl(var(--muted))]/30 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="font-semibold text-[hsl(var(--foreground))] text-base group-hover:text-blue-600 transition-colors">
                                                    {ticket.title}
                                                </span>
                                                <span className="text-xs font-mono text-[hsl(var(--muted-foreground))] mt-1">
                                                    ID: #{ticket?._id?.slice?.(-6).toUpperCase() || 'N/A'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-[hsl(var(--foreground))]">
                                            {ticket.organizationId?.name || 'Unknown Org'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <TicketStatusBadge status={ticket.status} />
                                        </td>
                                        <td className="px-6 py-4">
                                            <PriorityBadge priority={ticket.priority} />
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                onClick={() => navigate(`/support/tickets/${ticket._id}`)}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-[#0088cc]/18 border border-[hsl(var(--border))] text-[hsl(var(--foreground))] hover:border-blue-500 hover:text-blue-600 transition-all shadow-sm text-sm font-medium group-hover:shadow-md"
                                            >
                                                <MdVisibility className="w-4 h-4" />
                                                Manage
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

